export type PermissionsDialogMember = {
  id: string;
  login: string;
  role: string;
};

export type PermissionsDialogWorkspace = {
  id: string;
  name: string;
};

export const EDIT_ACCESS_TABS = [
  'campaigns',
  'offers',
  'landings',
  'flows',
  'trafficSources',
  'affiliateNetworks',
] as const;

export type EditAccessTab = (typeof EDIT_ACCESS_TABS)[number];

export const tabsSettings = [
  { single: 'campaign', plural: 'campaigns' },
  { single: 'trafficSource', plural: 'trafficSources' },
  { single: 'flow', plural: 'flows' },
  { single: 'landing', plural: 'landings' },
  { single: 'offer', plural: 'offers' },
  { single: 'affiliateNetwork', plural: 'affiliateNetworks' },
] as const;

export const TABS_MAP = tabsSettings.reduce(
  (result, item) => {
    result[item.plural] = item.single;
    return result;
  },
  {} as Record<string, string>,
);

export const TABS_UI_CONFIG: Record<
  EditAccessTab,
  { headerKey: string; titleKey: string; icon: string }
> = {
  campaigns: {
    headerKey: 'All campaigns',
    titleKey: 'Campaigns',
    icon: 'fa-bullseye-arrow',
  },
  trafficSources: {
    headerKey: 'All traffic sources',
    titleKey: 'Traffic sources',
    icon: 'fa-globe',
  },
  flows: {
    headerKey: 'All flows',
    titleKey: 'Flows',
    icon: 'fa-random',
  },
  landings: {
    headerKey: 'All Landings',
    titleKey: 'Landings',
    icon: 'fa-browser',
  },
  offers: {
    headerKey: 'All Offers',
    titleKey: 'Offers',
    icon: 'fa-badge-dollar',
  },
  affiliateNetworks: {
    headerKey: 'All affiliate networks',
    titleKey: 'Affiliate networks',
    icon: 'fa-users-class',
  },
};

export const ACCESS_LEVELS = [
  { value: 'inherit', priority: 0, labelKey: 'Inherit' },
  { value: 'deny', priority: 1, labelKey: 'Access denied' },
  { value: 'reportsOnly', priority: 2, labelKey: 'Reports only' },
  { value: 'readOnly', priority: 3, labelKey: 'Read only' },
  { value: 'fullAccess', priority: 4, labelKey: 'Full access' },
] as const;

export type AccessLevelValue = (typeof ACCESS_LEVELS)[number]['value'];

export type AccessOption = {
  text: string;
  value: AccessLevelValue;
  priority: number;
};

export type TabUiData = {
  header: string;
  title: string;
  icon: string;
};

type TranslateFn = (key: string) => string;

export const buildTabsData = (translate: TranslateFn): Record<EditAccessTab, TabUiData> => {
  const result = {} as Record<EditAccessTab, TabUiData>;

  EDIT_ACCESS_TABS.forEach((tabKey) => {
    const config = TABS_UI_CONFIG[tabKey];
    result[tabKey] = {
      header: translate(config.headerKey),
      title: translate(config.titleKey),
      icon: config.icon,
    };
  });

  return result;
};

export const buildAccessObj = (translate: TranslateFn): Record<AccessLevelValue, AccessOption> => {
  const result = {} as Record<AccessLevelValue, AccessOption>;

  ACCESS_LEVELS.forEach((level) => {
    result[level.value] = {
      text: translate(level.labelKey),
      value: level.value,
      priority: level.priority,
    };
  });

  return result;
};

export const getDefaultUserAlc = () => ({
  accessToExistingItems: 'fullAccess',
  hideProfit: false,
  showConversions: false,
  canCreate: {
    affiliateNetworks: true,
    campaigns: true,
    flows: true,
    landings: true,
    offers: true,
    trafficSources: true,
  },
  acl: {
    campaign: { '*': 'fullAccess' },
    trafficSource: { '*': 'fullAccess' },
    landing: { '*': 'fullAccess' },
    offer: { '*': 'fullAccess' },
    flow: { '*': 'fullAccess' },
    affiliateNetwork: { '*': 'fullAccess' },
  },
});

export const getDefaultItemsRelationsObject = () => ({
  campaigns: {
    '*': {
      uses: {
        flow: '*',
        landings: ['*'],
        offers: ['*'],
        trafficSource: '*',
      },
    },
  },
  trafficSources: {
    '*': {
      depends: {
        campaigns: ['*'],
      },
    },
  },
  landings: {
    '*': {
      depends: {
        campaigns: ['*'],
        flows: ['*'],
      },
    },
  },
  offers: {
    '*': {
      uses: {
        affiliateNetwork: '*',
      },
      depends: {
        campaigns: ['*'],
        flows: ['*'],
      },
    },
  },
  flows: {
    '*': {
      uses: {
        landings: ['*'],
        offers: ['*'],
      },
      depends: {
        campaigns: ['*'],
      },
    },
  },
  affiliateNetworks: {
    '*': {
      depends: {
        offers: ['*'],
      },
    },
  },
});

export const getDefaultFilteredRowsByTab = () => ({
  campaigns: [] as unknown[],
  trafficSources: [] as unknown[],
  flows: [] as unknown[],
  landings: [] as unknown[],
  offers: [] as unknown[],
  affiliateNetworks: [] as unknown[],
});
